import { router } from "expo-router";
import { useCallback, useMemo } from "react";
import { View } from "react-native";
import { useSharedValue } from "react-native-reanimated";

import Icon from "$src/core/components/atoms/Icon";
import Pressable from "$src/core/components/atoms/Pressable";
import Text from "$src/core/components/atoms/Text/Text";
import useAnalytics from "$src/core/hooks/use-analytics";
import useAuth from "$src/core/hooks/use-auth";
import useTranslation from "$src/core/hooks/use-translation";

import $, { useAnimatedStyles } from "./ProfileSelectionTeaser.styles";
import type { ProfileSelectionTeaserProps } from "./types";

const ProfileSelectionTeaser = ({
  profile,
  ref,
}: ProfileSelectionTeaserProps) => {
  const { t } = useTranslation();
  const { status: authStatus } = useAuth();
  const { trackEvent } = useAnalytics();

  const isFocused = useSharedValue(false);

  const { animatedCardStyle } = useAnimatedStyles({
    isFocused,
  });

  const handlePress = useCallback(() => {
    if (authStatus === "authenticated") {
      router.navigate("/profiles");
      trackEvent("button_interaction", {
        button_name: "switch_profile",
        button_type: "button_profile_selection",
        button_label: t("PROFILE_PAGE/BUTTON_SWITCH_PROFILE_LABEL"),
      });
    } else {
      router.navigate("/login");
      trackEvent("button_interaction", {
        button_name: "sign_in",
        button_type: "button_profile_sign_in",
        button_label: t("PROFILE_PAGE/BUTTON_SIGN_IN_LABEL"),
      });
    }
  }, [authStatus, trackEvent, t]);

  const AuthenticatedContent = useMemo(
    () => (
      <>
        {profile && (
          <View style={[$.circle, { backgroundColor: profile.tint }]}>
            <Icon name="person-outline" style={$.circleIcon} />
          </View>
        )}

        <View style={$.cardMiddle}>
          <Text preset="tiny" accessibilityRole="text" style={$.cardSubtext}>
            {t("PROFILE_PAGE/BUTTON_SWITCH_PROFILE_LABEL")}
          </Text>
          {profile && (
            <Text
              preset="heading5"
              accessibilityRole="text"
              accessibilityLabel={t("SETTINGS/CURRENT_PROFILE/HINT", {
                name: profile.name,
              })}
              style={$.cardText}
            >
              {profile.name}
            </Text>
          )}
        </View>

        <Icon name="switch" style={$.cardIcon} />
      </>
    ),
    [profile, t],
  );

  const UnauthenticatedContent = useMemo(
    () => (
      <>
        <Icon name="person-outline" style={$.cardIcon} />

        <Text preset="heading5" accessibilityRole="text" style={$.cardMiddle}>
          {t("PROFILE_PAGE/BUTTON_SIGN_IN_LABEL")}
        </Text>
      </>
    ),
    [t],
  );

  const Content =
    authStatus === "authenticated"
      ? AuthenticatedContent
      : UnauthenticatedContent;

  return (
    <Pressable
      ref={ref}
      accessibilityRole="button"
      onPress={handlePress}
      onFocus={() => isFocused.set(true)}
      onBlur={() => isFocused.set(false)}
      style={[$.card, animatedCardStyle]}
    >
      {Content}
    </Pressable>
  );
};

export default ProfileSelectionTeaser;
